import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import PollingTollingAdminAccess from 'components/PollingTollingAdminAccess';

import React, { Suspense, useEffect, useState } from 'react';
import HomePage from 'components/pages/HomePage';
import PageLayout from 'components/PageLayout';
import { Pages } from 'utils/pages.utils';

import useAuthStore from 'store/authStore';
import ProtectedRoute from 'components/ProtectedRoute';
import { PAGES_ENUM, USER_ROLES_ENUM } from 'utils/authenticationUtils';
import LoadingPage from 'components/pages/LoadingPage';
import LoginPage from 'components/pages/LoginPage';

// manual chunks defined in vite.config.ts
const BillingDetailsPage = React.lazy(() => import('pages/BillingDetailsPage'));
const BillingDetailsListPage = React.lazy(() => import('pages/BillingDetailsListPage'));

const PaymentClaimsPage = React.lazy(() => import('pages/PaymentClaimsPage'));
const PaymentClaimsListPage = React.lazy(() => import('pages/PaymentClaimsListPage'));

const PassageDetailsPage = React.lazy(() => import('pages/PassageDetailsPage'));
const PassagesListPage = React.lazy(() => import('pages/PassagesListPage'));

const ReceiptsDetailsPage = React.lazy(() => import('pages/ReceiptsDetailsPage'));
const ReceiptsListPage = React.lazy(() => import('pages/ReceiptsListPage'));

const ITicketDetailsPage = React.lazy(() => import('pages/ITicketDetailsPage'));
const ITicketListPage = React.lazy(() => import('pages/ITicketListPage'));

const TransactionDetailsPage = React.lazy(() => import('pages/TransactionDetailsPage'));
const TransactionListPage = React.lazy(() => import('pages/TransactionListPage'));

const ActionListDetailsPage = React.lazy(() => import('pages/ActionListDetailsPage'));
const ActionListsPage = React.lazy(() => import('pages/ActionListsPage'));

const ExceptionListInfoPage = React.lazy(() => import('pages/ExceptionListInfoPage'));
const ExceptionListListPage = React.lazy(() => import('pages/ExceptionListListPage'));

const ContractIssuerListsPage = React.lazy(() => import('pages/ContractIssuerListsPage'));
const ContractIssuerListsDetailsPage = React.lazy(
  () => import('pages/ContractIssuerListsDetailsPage')
);

const AdminPage = React.lazy(() => import('pages/AdminPage'));
const AdminUserGroupsPage = React.lazy(() => import('pages/AdminUserGroupsPage'));

const AuditLogListPage = React.lazy(() => import('pages/AuditLogListPage'));

const EfcContextDataInboundListPage = React.lazy(
  () => import('pages/EfcContextDataInboundListPage')
);
const EfcContextDataOutboundListPage = React.lazy(
  () => import('pages/EfcContextDataOutboundListPage')
);

const OresundsbronDetailsPage = React.lazy(() => import('pages/OresundsbronDetailsPage'));
const OresundsbronEditPage = React.lazy(() => import('pages/OresundsbronEditPage'));

const TollServiceProviderListPage = React.lazy(() => import('pages/TollServiceProviderListPage'));
const TollServiceProviderEditPage = React.lazy(() => import('pages/TollServiceProviderEditPage'));
const TollServiceProviderNewPage = React.lazy(() => import('pages/TollServiceProviderNewPage'));
const TollServiceProviderDetailsPage = React.lazy(
  () => import('pages/TollServiceProviderDetailsPage')
);

const LocalAgreementsListPage = React.lazy(() => import('pages/LocalAgreementsListPage'));
const LocalAgreementDetailsPage = React.lazy(() => import('pages/LocalAgreementDetailsPage'));

const LazyProtectedRoute = ({
  Component,
  page,
  role,
}: {
  Component: React.LazyExoticComponent<() => React.JSX.Element>;
  page?: PAGES_ENUM;
  role?: USER_ROLES_ENUM;
}) => (
  <Suspense fallback={<LoadingPage />}>
    <ProtectedRoute page={page} role={role}>
      <Component />
    </ProtectedRoute>
  </Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path={Pages.home} element={<PageLayout />}>
      <Route index element={<HomePage />} />

      <Route
        path={Pages.billingDetailsList}
        element={
          <LazyProtectedRoute Component={BillingDetailsListPage} page={PAGES_ENUM.billingDetails} />
        }
      />
      <Route
        path={Pages.billingDetails}
        element={
          <LazyProtectedRoute Component={BillingDetailsPage} page={PAGES_ENUM.billingDetails} />
        }
      />
      <Route
        path={Pages.paymentClaimsList}
        element={
          <LazyProtectedRoute Component={PaymentClaimsListPage} page={PAGES_ENUM.paymentClaims} />
        }
      />
      <Route
        path={Pages.paymentClaims}
        element={
          <LazyProtectedRoute Component={PaymentClaimsPage} page={PAGES_ENUM.paymentClaims} />
        }
      />

      <Route
        path={Pages.exceptionListList}
        element={
          <LazyProtectedRoute Component={ExceptionListListPage} page={PAGES_ENUM.exceptionLists} />
        }
      />
      <Route
        path={Pages.exceptionListDetails}
        element={
          <LazyProtectedRoute Component={ExceptionListInfoPage} page={PAGES_ENUM.exceptionLists} />
        }
      />

      <Route
        path={Pages.contractIssuerLists}
        element={
          <LazyProtectedRoute
            Component={ContractIssuerListsPage}
            page={PAGES_ENUM.contractIssuerLists}
          />
        }
      />
      <Route
        path={Pages.contractIssuerListDetails}
        element={
          <LazyProtectedRoute
            Component={ContractIssuerListsDetailsPage}
            page={PAGES_ENUM.contractIssuerLists}
          />
        }
      />

      <Route
        path={Pages.passagesList}
        element={
          <LazyProtectedRoute Component={PassagesListPage} page={PAGES_ENUM.passageReports} />
        }
      />
      <Route
        path={Pages.passageDetails}
        element={
          <LazyProtectedRoute Component={PassageDetailsPage} page={PAGES_ENUM.passageReports} />
        }
      />

      <Route
        path={Pages.receiptList}
        element={<LazyProtectedRoute Component={ReceiptsListPage} page={PAGES_ENUM.receipt} />}
      />
      <Route
        path={Pages.receiptDetails}
        element={<LazyProtectedRoute Component={ReceiptsDetailsPage} page={PAGES_ENUM.receipt} />}
      />

      <Route
        path={Pages.iTicketList}
        element={<LazyProtectedRoute Component={ITicketListPage} page={PAGES_ENUM.transaction} />}
      />
      <Route
        path={Pages.iTicketDetails}
        element={
          <LazyProtectedRoute Component={ITicketDetailsPage} page={PAGES_ENUM.transaction} />
        }
      />

      <Route
        path={Pages.transactionList}
        element={<LazyProtectedRoute Component={TransactionListPage} page={PAGES_ENUM.iTicket} />}
      />
      <Route
        path={Pages.transactionDetails}
        element={
          <LazyProtectedRoute Component={TransactionDetailsPage} page={PAGES_ENUM.iTicket} />
        }
      />

      <Route
        path={Pages.auditLog}
        element={<LazyProtectedRoute Component={AuditLogListPage} page={PAGES_ENUM.auditLog} />}
      />

      <Route
        path={Pages.actionLists}
        element={<LazyProtectedRoute Component={ActionListsPage} page={PAGES_ENUM.actionList} />}
      />
      <Route
        path={Pages.actionListDetails}
        element={
          <LazyProtectedRoute Component={ActionListDetailsPage} page={PAGES_ENUM.actionList} />
        }
      />

      <Route
        path={Pages.tollServiceProviderList}
        element={
          <LazyProtectedRoute
            Component={TollServiceProviderListPage}
            page={PAGES_ENUM.tollServiceProvider}
          />
        }
      />
      <Route
        path={Pages.tollServiceProviderDetails}
        element={
          <LazyProtectedRoute
            Component={TollServiceProviderDetailsPage}
            page={PAGES_ENUM.tollServiceProvider}
          />
        }
      />
      <Route
        path={Pages.tollServiceProviderNew}
        element={
          <LazyProtectedRoute
            Component={TollServiceProviderNewPage}
            page={PAGES_ENUM.tollServiceProvider}
          />
        }
      />
      <Route
        path={Pages.tollServiceProviderEdit}
        element={
          <LazyProtectedRoute
            Component={TollServiceProviderEditPage}
            page={PAGES_ENUM.tollServiceProvider}
          />
        }
      />

      <Route
        path={Pages.oresundsbronDataDetails}
        element={
          <LazyProtectedRoute
            Component={OresundsbronDetailsPage}
            page={PAGES_ENUM.oresundsbronData}
          />
        }
      />
      <Route
        path={Pages.oresundsbronDataEdit}
        element={
          <LazyProtectedRoute Component={OresundsbronEditPage} page={PAGES_ENUM.oresundsbronData} />
        }
      />

      <Route
        path={Pages.efcContextDataInbound}
        element={
          <LazyProtectedRoute
            Component={EfcContextDataInboundListPage}
            page={PAGES_ENUM.efcContextDataInbound}
          />
        }
      />
      <Route
        path={Pages.efcContextDataOutbound}
        element={
          <LazyProtectedRoute
            Component={EfcContextDataOutboundListPage}
            page={PAGES_ENUM.efcContextDataOutbound}
          />
        }
      />

      <Route
        path={Pages.adminPage}
        element={<LazyProtectedRoute Component={AdminPage} role={USER_ROLES_ENUM.userAdminGroup} />}
      />
      <Route
        path={Pages.adminUserGroups}
        element={
          <LazyProtectedRoute
            Component={AdminUserGroupsPage}
            role={USER_ROLES_ENUM.userAdminGroup}
          />
        }
      />

      <Route
        path={Pages.localAgreements}
        element={
          <LazyProtectedRoute
            Component={LocalAgreementsListPage}
            page={PAGES_ENUM.localAgreements}
          />
        }
      />
      <Route
        path={Pages.localAgreementDetails}
        element={
          <LazyProtectedRoute
            Component={LocalAgreementDetailsPage}
            page={PAGES_ENUM.localAgreements}
          />
        }
      />
    </Route>
  )
);

function Router() {
  const [signedInRoutineDone, setSignedInRoutineDone] = useState<boolean>(false);
  const { acquireToken, acquireTokenSso } = useAuthStore();

  useEffect(() => {
    const initAuth = async () => {
      try {
        let token = await acquireToken();
        if (import.meta.env.MODE !== 'production') {
          console.log('token', token);
        }
        if (!token) {
          token = await acquireTokenSso();
          if (import.meta.env.MODE !== 'production') {
            console.log('SSO token', token);
          }
        }
      } catch (error) {
        console.error('Authentication failed:', error);
      } finally {
        setSignedInRoutineDone(true);
      }
    };
    void initAuth();
  }, [acquireToken, acquireTokenSso]);

  if (!signedInRoutineDone) {
    return <LoadingPage />;
  }
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <PollingTollingAdminAccess />
        <RouterProvider router={router} />
      </AuthenticatedTemplate>
      {import.meta.env.VITE_ENVIRONMENT !== 'production' && (
        <div className="fixed top-5 right-0 border bg-white px-1 opacity-50">
          <div className="xs:visible sm:hidden">XS</div>
          <div className="max-sm:hidden sm:visible md:hidden">SM</div>
          <div className="max-md:hidden md:visible lg:hidden">MD</div>
          <div className="max-lg:hidden lg:visible xl:hidden">LG</div>
          <div className="max-xl:hidden xl:visible 2xl:hidden">XL</div>
          <div className="3xl:hidden max-2xl:hidden 2xl:visible">2XL</div>
          <div className="max-3xl:hidden 3xl:visible 4xl:hidden">3XL</div>
          <div className="max-4xl:hidden 4xl:visible 5xl:hidden">4XL</div>
          <div className="6xl:hidden max-5xl:hidden 5xl:visible">5XL</div>
        </div>
      )}
    </>
  );
}

export default Router;
